import { ProductOption } from '../..//submission-start-choice/types/program-types';

/**
 * Static product configuration for the submission-start-choice picker.
 * Moved out of the component so the component itself only contains
 * behaviour, not data.
 *
 * Icon note: all products currently point at the single placeholder SVG
 * (assets/icon1.svg) per the supplied asset. Once additional icons are
 * ready, just point each product's `icon` field at its own file - no
 * other code needs to change.
 */

const GUARDIAN_ELITE_ICON = '/assets/icons/shield.svg';
const DIC_EXCL_FIRE_ICON = '/assets/icons/dic.svg';
const DWELLING_FIRE_ICON = '/assets/icons/dwelling-fire.svg';
const VACANT_PROP_ICON = '/assets/icons/vacant-prop.svg';
const COURSE_OF_CONST_ICON = '/assets/icons/course-of-const.svg';
const VACANT_LAND_ICON = '/assets/icons/vacant-land.svg';
const BUILDERS_RENOVATION_ICON = '/assets/icons/builders-renovation.svg';

// ---- Green Shield ----
export const GUARDIAN_ELITE: ProductOption = {
  id: 'guardian-elite',
  group: 'green-shield',
  name: 'Guardian Elite',
  description: 'Owner-occupied primary and secondary residences',
  icon: GUARDIAN_ELITE_ICON,
  recommended: true,
  detail: {
    title: 'Guardian Elite',
    subtitle: 'Owner-occupied primary and secondary residences',
    productDescription: [
      { label: 'Coverage A', value: '$800,000 - $7,182,500' },
      { label: 'Max TIV', value: '$10,055,500' }
    ],
    tags: ['HO-3', 'CA/CO'],
    keyLimits: [
      { label: 'Cov A (Dwelling)', value: '$800,000 - $7,182,500' },
      { label: 'Cov B (Other Structures)', value: 'Up to 10%' },
      { label: 'Cov C (Contents)', value: 'Up to 20%' },
      { label: 'Cov D (Loss of Use)', value: 'Up to 10%' },
      { label: 'Liability', value: '$300K or $500K' },
      { label: 'AOP Deductible', value: '$5K - $100K' }
    ],
    ineligible: 'PC 8/9/10 \u22c5 Wood roofs \u22c5 Knob-and-tube or aluminum wiring \u22c5 Gal./cast iron/lead/poly plumbing \u22c5 EIFS \u22c5 Fuse boxes \u22c5 Built before 1925 \u22c5 Rental or vacant \u22c5 2+ losses in 5 years'
  }
};

// ---- Specialty Programs (backed by AU Gold) ----
export const SPECIALTY_PRODUCTS: ProductOption[] = [
  {
    id: 'dic-excluding-fire',
    group: 'specialty',
    name: 'DIC - Excluding Fire',
    description: 'Supplemental coverage layered over existing policy',
    icon: DIC_EXCL_FIRE_ICON,
    detail: {
      title: 'DIC - Excluding Fire',
      subtitle: 'Supplemental coverage layered over existing policy',
      tags: ['HO-3', 'HO-6', 'CA Only'],
      productDescription: [{ label: 'Max TIV', value: '$6M' }],
      keyLimits: [
        { label: 'Cov A (HO-3)', value: 'Up to $4.5M' },
        { label: 'Cov A (HO-6)', value: 'Up to $500K' }
      ],
      ineligible: 'TIV > $3M without excess fire \u22c5 Structural renovations \u22c5 Barrier island (wind) risks \u22c5 Modular/manufactured homes'
    }
  },
  {
    id: 'dwelling-fire',
    group: 'specialty',
    name: 'Dwelling Fire',
    description: 'Primary, Secondary, Seasonal, Rental, and Condo properties',
    icon: DWELLING_FIRE_ICON,
    detail: {
      title: 'Dwelling Fire',
      subtitle: 'Primary, Secondary, Seasonal, Rental, and Condo properties',
      tags: ['DP-1', 'DP-3', 'Up to 6 locations'],
      productDescription: null,
      keyLimits: [
        { label: 'Cov A (Building)', value: '$1M ($1.25M CA)' },
        { label: 'Condo', value: 'Up to $500K' },
        { label: 'Liability', value: 'Up to $1M' },
        { label: 'Cosmetic Renovation', value: 'Up to $150K' },
      ],
      ineligible: 'Min 100-amp circuit breakers required \u22c5 No knob & tube, aluminum wiring or fuse based panels \u22c5 Property must not exceed 25 acres'
    }
  },
  {
    id: 'vacant-property',
    group: 'specialty',
    name: 'Vacant Property',
    description: 'Unoccupied residential or commercial buildings',
    icon: VACANT_PROP_ICON,
    detail: {
      title: 'Vacant Property',
      subtitle: 'Unoccupied residential or commercial buildings',
      tags: ['Apts Up to 4 units', 'Mixed use', 'Up to 6 locations'],
      productDescription: null,
      keyLimits: [
        { label: 'Building', value: 'Up to $5M' },
        { label: 'Building (PC 9/10)', value: 'Up to $750K' },
        { label: 'Condo', value: 'Up to $500K' },
        { label: 'Cosmetic Renovation', value: '50% of value or $400K' },
      ],
      ineligible: 'Schools, golf clubs, condo associations, assisted living, hotels/motels, buildings > 30,000 sqft, pre-existing damage, no expectation of future occupancy'
    }
  },
  {
    id: 'vacant-land',
    group: 'specialty',
    name: 'Vacant Land',
    description: 'Premises liability for undeveloped land',
    icon: VACANT_LAND_ICON,
    detail: {
      title: 'Vacant Land',
      subtitle: 'Premises liability for undeveloped land',
      tags: ['Undeveloped', 'Unoccupied', 'Up to 500 acres'],
      productDescription: null,
      keyLimits: [
        { label: 'Premises Liability', value: 'Up to $1M' },
        { label: 'Aggregate', value: 'Up to $2M' },
      ],
      ineligible: 'Land with any activities, dump sites/landfills, environmental hazards, abandoned equipment, blasting/caves/mines/quarries/oil-gas wells/railroad, underground fuel storage, levees/dams > 1 acre, land leased to others, > 500 acres.'
    }
  },
  {
    id: 'course-of-construction',
    group: 'specialty',
    name: 'Course of Construction',
    description: 'New ground-up construction, residential and commercial',
    icon:  COURSE_OF_CONST_ICON,
    detail: {
      title: 'Course of Construction',
      subtitle: 'New ground-up construction, residential and commercial',
      tags: ['Residential', 'Commercial', 'Mixed use', '1 location per policy'],
      productDescription: null,
      keyLimits: [
        { label: 'Building', value: 'Up to $4M' },
        { label: 'Liability', value: 'Up to $1M / $2M agg.' },
      ],
      ineligible: 'Owner-builders, demolition/underpinning, pollutant abatement, projects >3 stories or 30,000 sqft, contractor without $1M liability limit.'
    }
  },
  {
    id: 'builders-renovation',
    group: 'specialty',
    name: 'Builder\u2019s Renovation',
    description: 'Existing structures under active renovation',
    icon: BUILDERS_RENOVATION_ICON,
    detail: {
      title: 'Builder\u2019s Renovation',
      subtitle: 'Existing structures under active renovation',
      tags: ['Residential', 'Commercial', 'Up to 6 locations'],
      productDescription: null,
      keyLimits: [
        { label: 'Building / TIV', value: 'Up to $5M' },
        { label: 'Building (PC 9/10)', value: 'Up to $750K' },
        { label: 'Liability', value: 'Up to $1M' },
        { label: 'Theft of materials', value: '$50K sublimit' },
        { label: 'Structural renovations', value: 'Up to 200% of value' },
        { label: 'Policy term', value: 'Up to 18 months' },
      ],
      ineligible: 'Hotels/motels, condo associations, assisted living, schools, structures >3 stories, >10,000 sq ft residential / >35,000 sq ft commercial.'
    }
  }
];
